import { JobTypes } from 'common/state/jobs/actions';
import { transparentize } from 'polished';
import styled, { css, keyframes } from 'styled-components';
import { getJobTypeColour } from '../config';

export const NoJobsMessage = styled.div<{ itemCount?: number }>`
  padding: 1.75rem 0 3.5rem;
  width: fill-available;
  height: ${({ itemCount = 4 }) => (itemCount - 1) * 2}rem;
  margin: 0.25rem;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.25;
`;

export const JobContainer = styled.div<{ type?: JobTypes }>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  margin: 0.25rem 0;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ type, theme }) => getJobTypeColour(type, theme)};
  align-items: center;
  font-size: 0.75rem;
  transition: background-color 300ms;
  text-transform: capitalize;
  cursor: pointer;
  box-sizing: border-box;
  height: 1.75rem;

  &:hover {
    background-color: ${({ type, theme }) =>
      getJobTypeColour(type, theme) &&
      transparentize(0.8, getJobTypeColour(type, theme))};
  }
`;

export const HiddenJobContainer = styled(JobContainer)<{ visible?: boolean }>`
  transition: background-color 300ms, opacity 300ms;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

export const ExpandJobContainer = styled(JobContainer)`
  border-color: transparent;
  color: ${({ theme }) => theme.primary};

  &:hover {
    background-color: ${({ theme }) => transparentize(0.9, theme.primary)};
  }
`;

export const CalendarContainer = styled.div`
  width: 100vw;
  height: 100%;
  padding: 1rem;

  box-sizing: border-box;
`;

export const CalendarControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid
    ${({ theme }) => transparentize(0.75, theme.foreground)};
  padding: 0.5rem;
`;

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  margin-right: 2rem;

  &:last-child {
    margin-right: 0;
  }
`;

export const GroupSpacer = styled.div`
  width: 1rem;
`;

export const CalendarGrid = styled.div<{ weeks: number }>`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 3rem repeat(7, 1fr);
  grid-template-rows: auto repeat(${({ weeks }) => weeks * 2}, auto);
  margin-top: 0.75rem;
`;

export const HeaderCell = styled.div`
  padding: 0.25rem;
  text-align: center;
  font-weight: bold;
  font-size: 0.75rem;
  text-transform: uppercase;
`;

const EntryKeyframes = keyframes`
    from { opacity: 0 }
    to   { opacity: 1 }
`;

export const MonthCell = styled.div<{ rowStart: number; rowEnd: number }>`
  grid-column: 1;
  display: flex;
  justify-content: flex-end;
    grid-row: ${({ rowStart }) => rowStart} / ${({ rowEnd }) => rowEnd};
}
`;

export const MonthCellContent = styled.div`
  box-sizing: border-box;
  height: calc(100% - 8px);
  display: flex;
  align-items: center;
  border-right: 3px solid
    ${({ theme }) => transparentize(0.75, theme.foreground)};
  padding: 0.5rem;
  margin: 4px 0;
  font-weight: bold;
  font-size: 0.75rem;
  text-transform: uppercase;
`;

export const DayCell = styled.div<{
  weekNo: number;
  dayNo: number;
}>`
  grid-row: span 2;
  padding: 0.5rem;
  border: solid ${({ theme }) => transparentize(0.95, theme.foreground)};
  border-left-width: ${({ dayNo }) => (dayNo === 0 ? '1px' : 0)};
  border-right-width: 1px;
  border-bottom-width: 1px;
  border-top-width: ${({ weekNo }) => (weekNo === 0 ? '1px' : 0)};
  cursor: pointer;
`;

export const DayCellContent = styled.div<{
  animate?: boolean;
}>`
  height: 100%;
  width: 100%;

  ${({ animate }) =>
    animate
      ? css`
          animation: ${EntryKeyframes} 1000ms forwards;
        `
      : undefined}
`;

export const DayCellDate = styled.div<{
  isToday?: boolean;
  isFuture?: boolean;
}>`
  width: 100%;
  text-align: center;
  font-weight: bold;
  font-size: 0.75rem;
  padding-bottom: 1rem;
  color: ${({ isToday, isFuture, theme }) =>
    isToday
      ? theme.primary
      : isFuture
      ? transparentize(0.75, theme.foreground)
      : undefined};
`;
